import { type AppliedAttributeFilter, type AttributeFilter } from '@kijiji/generated/graphql-types'
import AutosOtherIcon from '@kijiji/icons/src/icons/AutosOther'
import { type FC, type SyntheticEvent } from 'react'

import { FilterCheckboxList } from '@/components/srp/filters/filter-types/FilterCheckboxList'
import {
  AttributeFilterColorSwatch,
  ChipsFilterContainer,
} from '@/components/srp/filters/filter-types/styled'
import { MAX_CHIP_FILTERS } from '@/components/srp/filters/filter-types/useFieldsVisibility'
import { type FilterProps } from '@/components/srp/filters/FiltersAccordion/FiltersAccordion'
import { isValidColor } from '@/components/srp/filters/utils'
import { getAttributeFilterValuesCount } from '@/domain/filters'
import {
  ATTRIBUTE_FILTER_COLORS,
  AttributeColorVisualMap,
  ATTRIBUTES,
} from '@/features/attributes/constants/attributes'
import { ATTRIBUTES_CONFIG } from '@/features/attributes/constants/attributesConfig'
import { useParentFilter } from '@/hooks/filters/useParentFilter'
import { AccordionItem } from '@/ui/atoms/accordion'
import { FilterChip } from '@/ui/atoms/filter-chip'
import { SelectableIconChip } from '@/ui/atoms/selectable-icon-chip'

const carBodyTypeIconDictionary = ATTRIBUTES_CONFIG[ATTRIBUTES.CAR_BODY_TYPE].valueIcon || {}

export const MultiSelectFilter: FC<FilterProps<AttributeFilter, AppliedAttributeFilter>> = ({
  filter,
  parentFilter,
  isMobile,
  refetch,
  onClickAccordionItem,
}) => {
  const { name, label, selectedValues, values } = filter

  const { filterDescription } = useParentFilter({
    attributeFilter: filter,
    parentFilter,
    resetSelectedValues: () => refetch({ filterName: name, values: null }),
  })

  /**
   * Should render the options as chips if the filter is not of type child and has less than
   * the maximum number of options. Otherwise, the filter options should be rendered as
   * checkboxes.
   */
  const filterOptionsLessThanMax = getAttributeFilterValuesCount(filter) <= MAX_CHIP_FILTERS
  const shouldShowChips = !parentFilter && filterOptionsLessThanMax

  const options = values?.map((field, index) => {
    const canonicalValue = field.value
    const label = field.label
    const canonicalParentValue = `${canonicalValue}-${field.parentValue}`
    const checkBoxId = `${name}-${index}-${
      field.parentValue ? canonicalParentValue : canonicalValue
    }${isMobile ? '-mobile' : ''}`

    return {
      'data-testid': 'checkbox-multiselect-filter',
      id: checkBoxId,
      label,
      onChange: (e: SyntheticEvent<HTMLInputElement>) => {
        const selectedValue = (e.target as HTMLInputElement).value

        const updatedSelectedValues = selectedValues?.includes(selectedValue)
          ? selectedValues?.filter((v) => v !== selectedValue)
          : [...(selectedValues || []), selectedValue]

        const trackingLabel = `${name}=[${updatedSelectedValues.join(', ')}]`

        refetch({ filterName: name, values: updatedSelectedValues }, trackingLabel)
      },
      value: canonicalValue,
      checked: selectedValues?.includes(field.value),
    }
  })

  if (!options?.length) return null

  const renderChip = (item: NonNullable<typeof options>[number], index: number) => {
    if (name === ATTRIBUTES.CAR_BODY_TYPE) {
      const IconComponent = carBodyTypeIconDictionary[item.value] || AutosOtherIcon
      return <SelectableIconChip key={`${item.id}-${index}`} icon={<IconComponent />} {...item} />
    }

    if (name === ATTRIBUTES.CAR_COLOR) {
      const colorKey = isValidColor(item.value) ? item.value : ATTRIBUTE_FILTER_COLORS.OTHER
      const colorConfig = AttributeColorVisualMap[colorKey]

      return (
        <SelectableIconChip
          key={`${item.id}-${index}`}
          layout="horizontal"
          icon={
            <AttributeFilterColorSwatch
              data-testid="attribute-filter-color-swatch"
              colorValue={colorConfig.color}
              colorName={colorKey}
            />
          }
          {...item}
        />
      )
    }

    return <FilterChip key={`${item.id}-${index}`} {...item} />
  }

  return (
    <AccordionItem
      description={filterDescription}
      id={name}
      isDisabled={parentFilter && !parentFilter.isSelected}
      key={name}
      title={label || ''}
      onClick={onClickAccordionItem}
    >
      {!shouldShowChips ? (
        <FilterCheckboxList filter={filter} options={options} />
      ) : (
        <ChipsFilterContainer data-testid="chips-filter">
          {options.map(renderChip)}
        </ChipsFilterContainer>
      )}
    </AccordionItem>
  )
}
